import { Grid, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { AppConfig } from 'AppConfig';
import axios from 'axios';
import * as Constant from 'common/Constant';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { store } from 'store/store';
import { idsEqual } from 'utils/id';
import * as ValidationUtil from 'utils/ValidationUtil';
import * as MDActions from '../../common/actions/CommonActions';

const fundingOptions = [
  'Self Funded',
  'Fully Insured'
];

const PlanDesignDetail = (props) => {
  const DUPLICATION_ERROR_MSG = 'Same plan name for plan year is found.';

  const { tenantId, isModified, targetId, category, setSaveDisabled } = props;

  const { control, watch, formState, setValue, getValues, trigger } = useFormContext();

  const watchYearType = watch('ahpmId', null);

  const watchPlanEndDate = watch('planEndDate', null);
  const watchPlanStartDate = watch('planStartDate', null);

  const years = useSelector((state) => state.MD.mdCommon.allPlanYearsForTenant);
  const types = useSelector((state) => state.MD.mdCommon.allPlanTypes);

  const [selectedYear, setSelectedYear] = useState('');

  const handleYearUpdate = (yearDate) => {
    if (yearDate) {
      setValue(
        'planStartDate',
        yearDate.startDate ? moment(yearDate.startDate) : null
      );
      setValue(
        'planEndDate',
        yearDate.endDate ? moment(yearDate.endDate) : null
      );
    } else {
      setValue('planStartDate', null);
      setValue('planEndDate', null);
    }
  };

  useEffect(() => {
    store.dispatch(MDActions.fetchAllPlanYears(tenantId));
  }, [tenantId]);

  useEffect(() => {
    store.dispatch(MDActions.fetchAllPlanTypes(category));
  }, [category]);

  useEffect(() => {
    if (years && Array.isArray(years) && watchYearType) {
      const found = years.find((item) => idsEqual(item.id, watchYearType));
      if (found) {
        setSelectedYear(found.planYearName);
      }
    }
  }, [years, watchYearType]);

  return (
    <>
      <Grid container spacing={4}>
        <Grid item xs={6}>
          {isModified === true ? (
            <TextField
              id="readOnly-year"
              fullWidth
              disabled
              required
              value={selectedYear}
              label="Plan Year"
            />
          ) : (
            <Controller
              name="ahpmId"
              rules={{
                required: Constant.ERROR_MSG_REQUIRED_FIELD,
                validate: async (value) => {
                  let planName = getValues('planName');
                  if (planName) {
                    try {
                      setSaveDisabled(true);
                      const isFound = await axios.get(
                        AppConfig.API_BASE_URL +
                          `v2/plan/validate/duplication/master-id/${encodeURIComponent(value)}/name/${encodeURIComponent(planName)}`
                      );
                      return ValidationUtil.VALIDATION_DUPLICATION(
                        isFound,
                        targetId,
                        DUPLICATION_ERROR_MSG
                      );
                    } catch (error) {
                      console.log(error);
                    } finally {
                      setSaveDisabled(false);
                    }
                  }
                }
              }}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  id="input-year"
                  required
                  selectOnFocus
                  clearOnBlur
                  autoSelect
                  options={years}
                  value={
                    years.find((item) => idsEqual(item.id, field.value)) || null
                  }
                  isOptionEqualToValue={(option, value) =>
                    idsEqual(option.id, value.id)
                  }
                  onChange={(_, data) => {
                    handleYearUpdate(data);
                    field.onChange(data ? data.id : null);
                  }}
                  getOptionLabel={(option) =>
                    option.planYearName ? option.planYearName : ''
                  }
                     slotProps = {{
                          listbox: {
                              sx: {
                                '& .MuiAutocomplete-option, & .MuiAutocomplete-option *, && .MuiAutocomplete-option[aria-selected="true"], & .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover ': {
                                  color: '#000000 !important'
                                },
                                '& .MuiAutocomplete-option[aria-selected="true"]': {
                                  backgroundColor: '#5470b8 !important',
                                },
                                '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover': {
                                  backgroundColor: '#f4f5f6 !important',
                                }
                              }
                          }
                      }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      error={!!error}
                      helperText={error ? error.message : null}
                      label="Plan Year"
                    />
                  )}
                />
              )}
            />
          )}
        </Grid>
        <Grid item xs={6}>
          <Controller
            rules={{
              required: Constant.ERROR_MSG_REQUIRED_FIELD,
              validate: async (value) => {
                let ahpmId = getValues('ahpmId');
                if (ahpmId) {
                  try {
                    setSaveDisabled(true);
                    const isFound = await axios.get(
                      AppConfig.API_BASE_URL +
                        `v2/plan/validate/duplication/master-id/${encodeURIComponent(ahpmId)}/name/${encodeURIComponent(value)}`
                    );
                    return ValidationUtil.VALIDATION_DUPLICATION(
                      isFound,
                      targetId,
                      DUPLICATION_ERROR_MSG
                    );
                  } catch (error) {
                    console.log(error);
                  } finally {
                    setSaveDisabled(false);
                  }
                }
              }
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                id="input-name"
                fullWidth
                {...field}
                required
                error={!!error}
                helperText={error ? error.message : null}
                label="Plan Name"
              />
            )}
            name="planName"
            control={control}
          />
        </Grid>
        {category !== Constant.PLAN_DESIGN_DATA_TYPE.VISION ? (
          <Grid item xs={6}>
            <Controller
              rules={{ required: Constant.ERROR_MSG_REQUIRED_FIELD }}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  id="input-type"
                  options={types}
                  value={
                    types.find((item) => item === field.value) || null
                  }
                  autoSelect
                  onChange={(_, data) => field.onChange(data)}
                  slotProps = {{
                      listbox: {
                          sx: {
                            '& .MuiAutocomplete-option, & .MuiAutocomplete-option *, && .MuiAutocomplete-option[aria-selected="true"], & .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover ': {
                              color: '#000000 !important'
                            },
                            '& .MuiAutocomplete-option[aria-selected="true"]': {
                              backgroundColor: '#5470b8 !important',
                            },
                            '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover': {
                              backgroundColor: '#f4f5f6 !important',
                            }
                          }
                      }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Plan Type"
                      error={!!error}
                      required
                      helperText={error ? error.message : null}
                    />
                  )}
                />
              )}
              name="planType"
              control={control}
            />
          </Grid>
        ) : null}
        <Grid item xs={6}>
          <Controller
            rules={{
              required: Constant.ERROR_MSG_REQUIRED_FIELD,
              maxLength: {
                value: 3000,
                message: 'Exceed Max. 3000 Characters.'
              }
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                id="input-tpaNumber"
                label="TPA Plan Number"
                fullWidth
                {...field}
                required
                error={!!error}
                multiline
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
                helperText={error ? error.message : null}
              />
            )}
            name="planNumber"
            control={control}
            defaultValue=""
          />
        </Grid>
        <Grid item xs={6}>
            <Controller
              rules={{ required: Constant.ERROR_MSG_REQUIRED_FIELD }}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  id="input-type"
                  options={fundingOptions}
                  getOptionLabel={option => option}
                  {...field}
                  autoSelect
                  onChange={(_, data) => field.onChange(data)}
                 slotProps = {{
                      listbox: {
                          sx: {
                            '& .MuiAutocomplete-option, & .MuiAutocomplete-option *, && .MuiAutocomplete-option[aria-selected="true"], & .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover ': {
                              color: '#000000 !important'
                            },
                            '& .MuiAutocomplete-option[aria-selected="true"]': {
                              backgroundColor: '#5470b8 !important',
                            },
                            '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover': {
                              backgroundColor: '#f4f5f6 !important',
                            }
                          }
                      }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Plan Funding"
                      error={error}
                      required
                      helperText={error ? error.message : null}
                    />
                  )}
                />
              )}
              name="planFunding"
              control={control}
            />
        </Grid>
        <Grid item xs={6}>
          <Controller
            rules={{
              required: Constant.ERROR_MSG_REQUIRED_FIELD,
              validate: {
                planStartDate: (value) =>
                  ValidationUtil.START_DATE(
                    selectedYear,
                    value,
                    getValues('planEndDate'),
                    true
                  )
              }
            }}
            render={({ field, fieldState: { error } }) => (
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  {...field}
                  label="Start Date"
                  id="input-planStartDate"
                  maxDate={watchPlanEndDate ? moment.utc(watchPlanEndDate) : undefined}
                  inputFormat={Constant.FORMAT_DATE}
                  value={getValues('planStartDate') ? moment.utc(getValues('planStartDate')) : null}
                  onChange={(date) => {
                    field.onChange(date);
                    trigger('planStartDate');
                  }}
                  onBlur={field.onBlur}
                  slotProps={{
                    textField: {
                      required: true,
                      error: !!error,
                      helperText: error?.message ?? null,
                      onBlur: field.onBlur
                    }
                  }}
                />
              </LocalizationProvider>
            )}
            name="planStartDate"
            control={control}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            rules={{
              required: Constant.ERROR_MSG_REQUIRED_FIELD,
              validate: {
                planEndDate: (value) =>
                  ValidationUtil.END_DATE(
                    selectedYear,
                    getValues('planStartDate'),
                    value,
                    true
                  )
              }
            }}
            render={({ field, fieldState: { error } }) => (
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  {...field}
                  label="End Date"
                  id="input-planEndDate"
                  minDate={watchPlanStartDate ? moment.utc(watchPlanStartDate) : undefined}
                  inputFormat={Constant.FORMAT_DATE}
                  value={getValues('planEndDate') ? moment.utc(getValues('planEndDate')) : null}
                  onChange={(date) => {
                    field.onChange(date);
                    trigger('planEndDate');
                  }}
                  onBlur={field.onBlur}
                  slotProps={{
                    textField: {
                      required: true,
                      error: !!error,
                      helperText: error?.message ?? null,
                      onBlur: field.onBlur
                    }
                  }}
                />
              </LocalizationProvider>
            )}
            name="planEndDate"
            control={control}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default PlanDesignDetail;
